import {defineStore} from 'pinia';
import {usePage} from '@inertiajs/vue3';

const orderTypes = {
    bestseller: {
        type: 'bestseller',
        title: 'По популярности',
    },
    desc: {
        type: 'asc',
        title: 'Сначала дороже',
    },
    asc: {
        type: 'desc',
        title: 'По дешевле',
    },
}

const perPageArray = [15, 30, 50, 100]

const defaultFilterData = {
    base_categories: [],
    categories: [],
    sub_categories: [],
    per_page: 30,
    sort: orderTypes.bestseller.type,
    price: [0, 0],
    length: [0, 0],
    width: [0, 0],
    height: [0, 0],
    weight: [0, 0],
}

export const useCatalogStore = defineStore('catalogStore', {
    state: () => ({
        is_filter_open: false,
        loadings: {
            catalog_loaded: false,
            base_categories_loaded: false,
            filter_limits_loaded: false,
        },
        total_result: 0,
        order_types: orderTypes,
        per_page_array: perPageArray,
        filter_data: {
            base_categories: null,
            categories: [],
            sub_categories: [],

            //Данные которые задают лимиты фильтру, например min и max для слайдеров
            limits: {
                price: [0, 0],
                length: [0, 0],
                width: [0, 0],
                height: [0, 0],
                weight: [0, 0],
            },
        },
        filter: JSON.parse(JSON.stringify(defaultFilterData)),
        pagination: {
            current_page: 1,
            total_pages: 1,
        },
        timeout_events: {
            filter: undefined,
            pagination: undefined,
        },
        products: [],
        selected_filters: [],
    }),
    actions: {
        toggleFilter() {
            this.is_filter_open = !this.is_filter_open
        },
        onPageChange(page) {
            this.pagination.current_page = page
            window.scrollTo({top: 0, behavior: 'smooth'})
        },
        changeOrder(type) {
            this.filter.sort = type
        },
        changePerPage(per_page) {
            this.filter.per_page = per_page
        },
        hasUrlParams() {
            const page = usePage(); // Получаем текущий URL от Inertia
            const queryParams = new URLSearchParams(page.url.split('?')[1] || '');
            return queryParams.toString().length > 0; // Если есть хотя бы один параметр, возвращает true
        },
        syncUrlParamsWithState() {
            if (this.hasUrlParams()) {
                const page = usePage(); // Получаем текущие props от Inertia
                const queryParams = new URLSearchParams(page.url.split('?')[1] || '');

                this.filter.base_categories = queryParams.has('base_categories') ? JSON.parse(queryParams.get('base_categories')) : defaultFilterData.base_categories;
                this.filter.categories = queryParams.has('categories') ? JSON.parse(queryParams.get('categories')) : defaultFilterData.categories;
                this.filter.sub_categories = queryParams.has('sub_categories') ? JSON.parse(queryParams.get('sub_categories')) : defaultFilterData.sub_categories;
                this.filter.per_page = queryParams.has('per_page') ? queryParams.get('per_page') : defaultFilterData.per_page;
                this.filter.sort = queryParams.has('sort') ? queryParams.get('sort') : orderTypes.bestseller.type;
            }
        },
        //Функция, которая проверит наличие данных в параметрах ссылки
        getParamFromUrl(param, defaultValue) {
            const page = usePage(); // Получаем текущие props от Inertia
            const queryParams = new URLSearchParams(page.url.split('?')[1] || '');

            return queryParams.has(param) ? parseInt(queryParams.get(param)) : defaultValue
        },
        getFilterLimits() {
            if (!this.loadings.filter_limits_loaded) {
                axios.get('/axios/catalog-min-max')
                    .then(response => {
                        //Расставляем данные цен
                        this.filter_data.limits.price[0] = response.data.min_price ?? 0
                        this.filter_data.limits.price[1] = response.data.max_price ?? 0
                        this.filter.price[0] = this.getParamFromUrl('price_from', response.data.min_price ?? 0)
                        this.filter.price[1] = this.getParamFromUrl('price_to', response.data.max_price ?? 0)

                        //Расставляем данные длины товара
                        this.filter_data.limits.length[0] = response.data.min_length ?? 0
                        this.filter_data.limits.length[1] = response.data.max_length ?? 0
                        this.filter.length[0] = this.getParamFromUrl('length_from', response.data.min_length ?? 0)
                        this.filter.length[1] = this.getParamFromUrl('length_to', response.data.max_length ?? 0)

                        //Расставляем данные ширины товара
                        this.filter_data.limits.width[0] = response.data.min_width ?? 0
                        this.filter_data.limits.width[1] = response.data.max_width ?? 0
                        this.filter.width[0] = parseInt(this.getParamFromUrl('width_from', response.data.min_width ?? 0))
                        this.filter.width[1] = parseInt(this.getParamFromUrl('width_to', response.data.max_width ?? 0))

                        //Расставляем данные высоты товара
                        this.filter_data.limits.height[0] = response.data.min_height ?? 0
                        this.filter_data.limits.height[1] = response.data.max_height ?? 0
                        this.filter.height[0] = this.getParamFromUrl('height_from', response.data.min_height ?? 0)
                        this.filter.height[1] = this.getParamFromUrl('height_to', response.data.max_height ?? 0)

                        this.loadings.filter_limits_loaded = true
                    })
                    .catch(error => {
                        this.loadings.filter_limits_loaded = false
                        console.log(error.message)
                    })
            }
        },
        getFilterBasicCategories() {
            if (!this.loadings.base_categories_loaded) {
                axios.get('/axios/catalog-categories')
                    .then(response => {
                        this.loadings.base_categories_loaded = true
                        this.filter_data.base_categories = response.data
                    })
                    .catch(error => {
                        this.loadings.base_categories_loaded = false
                        console.log(error.message)
                    })
            }
        },
        getCatalog() {
            let is_all_data_loaded = this.loadings.base_categories_loaded
                && this.loadings.filter_limits_loaded

            if (is_all_data_loaded) {
                this.loadings.catalog_loaded = false
                let search_categories = [
                    ...this.filter.base_categories,
                    ...this.filter.categories,
                    ...this.filter.sub_categories
                ]

                const params = {
                    page: this.pagination.current_page,
                    per_page: this.filter.per_page,
                    length_from: this.filter.length[0],
                    length_to: this.filter.length[1],
                    width_from: this.filter.width[0],
                    width_to: this.filter.width[1],
                    weight_from: this.filter.weight[0],
                    weight_to: this.filter.weight[1],
                    height_from: this.filter.height[0],
                    height_to: this.filter.height[1],
                    price_from: this.filter.price[0],
                    price_to: this.filter.price[1],
                    sort: this.filter.sort
                };

                if (history.pushState) {
                    const queryString = new URLSearchParams({
                        ...params,
                        base_categories: JSON.stringify(this.filter.base_categories),
                        categories: JSON.stringify(this.filter.categories),
                        sub_categories: JSON.stringify(this.filter.sub_categories),
                    }).toString();

                    var newurl = window.location.protocol + "//" + window.location.host + window.location.pathname + '?' + queryString;
                    window.history.pushState({path: newurl}, '', newurl);
                }

                axios.get('/axios/catalog', {
                    params: {
                        ...params,
                        categories: search_categories,
                    }
                }).then(response => {
                    //Данные пагинации
                    this.pagination.total_pages = response.data.last_page ?? 1
                    //Количество найденных товаров
                    this.total_result = response.data.total ?? 0

                    this.filter_data.categories = []
                    this.filter_data.sub_categories = []

                    if (Array.isArray(response.data.categories)) {
                        response.data.categories.forEach(category => {
                            if (category.children !== null && category.children.length > 0) {
                                if (category.category.depth === 0) {
                                    category.children.forEach(category => {
                                        if (!this.filter_data.categories.some(p => p.id === category.id)) {
                                            this.filter_data.categories.push(category)
                                        }
                                    })
                                }
                                if (category.category.depth === 1) {
                                    category.children.forEach(category => {
                                        if (!this.filter_data.sub_categories.some(p => p.id === category.id)) {
                                            this.filter_data.sub_categories.push(category)
                                        }
                                    })
                                }
                            }

                            if (Array.isArray(category.ancestors)) {
                                category.ancestors.forEach(ancestor => {
                                    ancestor.children.forEach(children => {
                                        if (ancestor.depth === 0) {
                                            if (!this.filter_data.categories.some(p => p.id === children.id)) {
                                                this.filter_data.categories.push(children)
                                            }
                                        }
                                        if (ancestor.depth === 1) {
                                            if (!this.filter_data.sub_categories.some(p => p.id === children.id)) {
                                                this.filter_data.sub_categories.push(children)
                                            }
                                        }
                                    })
                                })
                            }
                        })
                    }

                    this.products = Array.isArray(response.data.products) ? response.data.products : []

                    this.updateSelectedFilters()

                    this.loadings.catalog_loaded = true
                }).catch(error => {
                    this.loadings.catalog_loaded = false

                    console.log(error.message)
                })
            }
        },
        removeNestedCheckBoxes(event, categoryType) {
            if (!event.target.checked) {
                if (categoryType === 'base_categories') {
                    this.removeBaseCategoriesNestedCheckBoxes(event.target.value)
                } else if (categoryType === 'categories') {
                    this.removeCategoryNestedCheckBoxes(event.target.value)
                }
            }
        },
        removeBaseCategoriesNestedCheckBoxes(slug) {
            let findBySlug = this.filter_data.base_categories.find(obj => obj.slug === slug);
            let getParentId = findBySlug ? findBySlug.id : null;

            let allSubCategories = this.filter_data.categories.filter(item => item.parent_id === getParentId);
            if (Array.isArray(allSubCategories)) {
                allSubCategories.forEach(category => {
                    this.filter.categories = this.filter.categories.filter(item => item !== category.slug);
                    this.removeCategoryNestedCheckBoxes(category.slug)
                });
            }
        },
        removeCategoryNestedCheckBoxes(slug) {
            let findBySlug = this.filter_data.categories.find(obj => obj.slug === slug);
            let getParentId = findBySlug ? findBySlug.id : null;

            let allSubCategories = this.filter_data.sub_categories.filter(item => item.parent_id === getParentId);
            if (Array.isArray(allSubCategories)) {
                allSubCategories.forEach(category => {
                    this.filter.sub_categories = this.filter.sub_categories.filter(item => item !== category.slug);
                });
            }
        },
        resetFilter() {
            this.filter.sort = orderTypes.bestseller.type

            this.filter.price[0] = this.filter_data.limits.price[0]
            this.filter.price[1] = this.filter_data.limits.price[1]
            this.filter.length[0] = this.filter_data.limits.length[0]
            this.filter.length[1] = this.filter_data.limits.length[1]
            this.filter.width[0] = this.filter_data.limits.width[0]
            this.filter.width[1] = this.filter_data.limits.width[1]
            this.filter.height[0] = this.filter_data.limits.height[0]
            this.filter.height[1] = this.filter_data.limits.height[1]

            this.filter.base_categories = defaultFilterData.base_categories
            this.filter.categories = defaultFilterData.categories
            this.filter.sub_categories = defaultFilterData.sub_categories
        },
        updateSelectedFilters() {
            this.selected_filters = [];

            const findName = (slug, list) => {
                if (!list) {
                    return
                }
                const item = list.find((el) => el.slug === slug);
                return item ? item.name : slug;
            };

            this.filter.base_categories.forEach((slug) => {
                this.selected_filters.push({
                    type: 'base_category',
                    value: slug,
                    title: `${findName(slug, this.filter_data.base_categories)}`
                });
            });

            this.filter.categories.forEach((slug) => {
                this.selected_filters.push({
                    type: 'category',
                    value: slug,
                    title: `${findName(slug, this.filter_data.categories)}`
                });
            });

            this.filter.sub_categories.forEach((slug) => {
                this.selected_filters.push({
                    type: 'sub_category',
                    value: slug,
                    title: `${findName(slug, this.filter_data.sub_categories)}`
                });
            });

            if (this.filter.price[0] > this.filter_data.limits.price[0] || this.filter.price[1] < this.filter_data.limits.price[1]) {
                this.selected_filters.push({
                    type: 'price',
                    value: `${this.filter.price[0]} - ${this.filter.price[1]}`,
                    title: `Цена: ${this.filter.price[0]} - ${this.filter.price[1]} ₽`
                });
            }

            if (this.filter.length[0] > this.filter_data.limits.length[0] || this.filter.length[1] < this.filter_data.limits.length[1]) {
                this.selected_filters.push({
                    type: 'length',
                    value: `${this.filter.length[0]} - ${this.filter.length[1]}`,
                    title: `Длина: ${this.filter.length[0]} - ${this.filter.length[1]} см`
                });
            }

            if (this.filter.width[0] > this.filter_data.limits.width[0] || this.filter.width[1] < this.filter_data.limits.width[1]) {
                this.selected_filters.push({
                    type: 'width',
                    value: `${this.filter.width[0]} - ${this.filter.width[1]}`,
                    title: `Ширина: ${this.filter.width[0]} - ${this.filter.width[1]} см`
                });
            }

            if (this.filter.height[0] > this.filter_data.limits.height[0] || this.filter.height[1] < this.filter_data.limits.height[1]) {
                this.selected_filters.push({
                    type: 'height',
                    value: `${this.filter.height[0]} - ${this.filter.height[1]}`,
                    title: `Высота: ${this.filter.height[0]} - ${this.filter.height[1]} см`
                });
            }
        },
        removeFilter(tag) {
            if (tag.type === 'base_category') {
                this.filter.base_categories = this.filter.base_categories.filter((item) => item !== tag.value);
                this.removeBaseCategoriesNestedCheckBoxes(tag.value);
            }
            if (tag.type === 'category') {
                this.filter.categories = this.filter.categories.filter((item) => item !== tag.value);
                this.removeCategoryNestedCheckBoxes(tag.value);
            }
            if (tag.type === 'sub_category') {
                this.filter.sub_categories = this.filter.sub_categories.filter((item) => item !== tag.value);
            }
            if (tag.type === 'price') {
                this.filter.price[0] = this.filter_data.limits.price[0]
                this.filter.price[1] = this.filter_data.limits.price[1]
            }
            if (tag.type === 'length') {
                this.filter.length[0] = this.filter_data.limits.length[0]
                this.filter.length[1] = this.filter_data.limits.length[1]
            }
            if (tag.type === 'width') {
                this.filter.width[0] = this.filter_data.limits.width[0]
                this.filter.width[1] = this.filter_data.limits.width[1]
            }
            if (tag.type === 'height') {
                this.filter.height[0] = this.filter_data.limits.height[0]
                this.filter.height[1] = this.filter_data.limits.height[1]
            }

            this.updateSelectedFilters();
        },
    }
});
